// src/components/theme.js
// Single source of truth for all colors, spacing, and style tokens.
// Import everywhere: import { T, S } from '@components/theme'

export const T = {
  // Brand
  amber:      '#d97706',
  amberLight: 'rgba(217,119,6,0.10)',
  red:        '#dc2626',
  redLight:   'rgba(220,38,38,0.08)',
  green:      '#059669',
  greenLight: 'rgba(5,150,105,0.08)',
  blue:       '#0369a1',
  blueLight:  'rgba(3,105,161,0.08)',

  // Surfaces
  bg:   '#f0ebe2',
  bg2:  '#e8dfc8',
  bg3:  '#d9d0bc',
  card: '#ffffff',

  // Text
  text:  '#1c1917',
  text2: '#44403c',
  dim:   '#78716c',

  // Borders
  bdr: 'rgba(0,0,0,0.09)',

  // Typography
  font: "-apple-system, 'SF Pro Display', system-ui, sans-serif",

  // Severity shape/pattern for colorblind mode (data-sev attribute + CSS)
  // Returns { shape, pattern, label } for non-color indicators
  sevShape: (level) => {
    if (level >= 5) return { shape: '▲', label: 'CRITICAL', pattern: 'diagonal-stripes' };
    if (level >= 4) return { shape: '■', label: 'HIGH',     pattern: 'diagonal-stripes' };
    if (level >= 3) return { shape: '◆', label: 'MODERATE', pattern: 'dots' };
    if (level >= 2) return { shape: '●', label: 'LOW',      pattern: 'none' };
    return                { shape: '○', label: 'INFO',      pattern: 'none' };
  },

  // Severity colors (indexed 0-5)
  sev: (level) => {
    if (level >= 5) return '#7f1d1d';
    if (level >= 4) return '#dc2626';
    if (level >= 3) return '#ea580c';
    if (level >= 2) return '#d97706';
    return '#059669';
  },
};

// ── Shared style objects ──────────────────────────────────────────
export const S = {
  // Full-height page container
  page: {
    animation: 'fadeIn 0.18s ease-out',
    display:        'flex',
    flexDirection:  'column',
    height:         '100%',
    background:     T.bg,
    overflow:       'hidden',
  },

  // Scrollable content area
  scroll: {
    flex:      1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },

  // Card
  card: {
    background:   T.card,
    borderRadius: 12,
    boxShadow:    `0 1px 4px ${T.bdr}`,
  },

  // ── Button system ────────────────────────────────────────────
  // Usage: style={{ ...S.btn() }}  or  style={{ ...S.btnOutline() }}

  // Primary — filled, full width
  btn: (color = T.amber, opts = {}) => ({
    width:        '100%',
    padding:      '13px',
    borderRadius: 10,
    border:       'none',
    background:   color,
    color:        '#fff',
    fontSize:     14,
    fontWeight:   800,
    cursor:       'pointer',
    fontFamily:   T.font,
    letterSpacing: 0.3,
    ...opts,
  }),

  // Secondary — outlined, full width
  btnOutline: (color = T.amber, opts = {}) => ({
    width:        '100%',
    padding:      '12px',
    borderRadius: 10,
    border:       `1.5px solid ${color}`,
    background:   'transparent',
    color:        color,
    fontSize:     13,
    fontWeight:   700,
    cursor:       'pointer',
    fontFamily:   T.font,
    ...opts,
  }),

  // Small action button — inline, not full width
  btnSm: (color = T.amber, opts = {}) => ({
    padding:      '5px 12px',
    borderRadius: 7,
    border:       `1px solid ${color}40`,
    background:   color + '12',
    color:        color,
    fontSize:     11,
    fontWeight:   700,
    cursor:       'pointer',
    fontFamily:   T.font,
    flexShrink:   0,
    ...opts,
  }),

  // Ghost — no border, no background, text only
  btnGhost: (color = T.dim, opts = {}) => ({
    padding:      '5px 8px',
    borderRadius: 6,
    border:       'none',
    background:   'none',
    color:        color,
    fontSize:     11,
    fontWeight:   600,
    cursor:       'pointer',
    fontFamily:   T.font,
    ...opts,
  }),

  // Destructive — red filled
  btnDanger: (opts = {}) => ({
    width:        '100%',
    padding:      '13px',
    borderRadius: 10,
    border:       'none',
    background:   '#dc2626',
    color:        '#fff',
    fontSize:     14,
    fontWeight:   900,
    cursor:       'pointer',
    fontFamily:   T.font,
    boxShadow:    '0 4px 16px rgba(220,38,38,0.35)',
    ...opts,
  }),

  // Removed old btn — kept below for legacy
  // Primary button factory (legacy)
  _btn: (color = T.amber, opts = {}) => ({
    width:        '100%',
    padding:      '13px',
    borderRadius: 10,
    border:       'none',
    background:   color,
    color:        '#fff',
    fontSize:     14,
    fontWeight:   800,
    cursor:       'pointer',
    fontFamily:   T.font,
    letterSpacing: 0.3,
    ...opts,
  }),

  // Filter pill factory
  pill: (active, color = T.amber) => ({
    padding:      '5px 12px',
    borderRadius: 20,
    border:       `1.5px solid ${active ? color : T.bdr}`,
    background:   active ? color : T.bg2,
    color:        active ? '#fff' : T.dim,
    fontSize:     11,
    fontWeight:   active ? 800 : 600,
    cursor:       'pointer',
    fontFamily:   T.font,
    transition:   'all 0.15s',
    whiteSpace:   'nowrap',
  }),

  // Section header (small caps label)
  sectionHeader: {
    fontSize:      10,
    fontWeight:    900,
    color:         T.dim,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    padding:       '8px 16px 4px',
  },

  // Input
  input: {
    width:        '100%',
    padding:      '10px 12px',
    borderRadius: 8,
    border:       `1.5px solid ${T.bdr}`,
    fontFamily:   T.font,
    fontSize:     13,
    color:        T.text,
    background:   T.bg2,
    outline:      'none',
    boxSizing:    'border-box',
  },

  // Toggle switch (pass isOn boolean)
  toggle: (isOn, color = T.green) => ({
    track: {
      width: 44, height: 26, borderRadius: 13, border: 'none', flexShrink: 0,
      background: isOn ? color : T.bg3, cursor: 'pointer',
      position: 'relative', transition: 'background 0.2s',
    },
    thumb: {
      position: 'absolute', top: 3, width: 20, height: 20,
      borderRadius: 10, background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      left: isOn ? 21 : 3, transition: 'left 0.2s',
    },
  }),
};
